import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StarField } from "@/components/alignment/StarField";
import { BackButton } from "@/components/BackButton";
import { PlanPanel } from "@/components/PlanPanel";
import { Seo } from "@/components/Seo";
import { BookOpen, Waves, Sparkles, Brain, Flame, ArrowRight, Sprout } from "lucide-react";

const MINDFUL_GAMES = [
  { id: "wisdom", slug: "wisdom-rush", name: "Wisdom Rush", tagline: "Practice discernment", color: "#A78BFA", icon: Brain },
  { id: "calm", slug: "calm-current", name: "Calm Current", tagline: "Return to the breath", color: "#2DD4BF", icon: Waves },
  { id: "karma", slug: "karma-garden", name: "Karma Garden", tagline: "Cultivate qualities", color: "#7BC96F", icon: Sprout },
];

const INTENTIONS = [
  "Notice before reacting.",
  "Meet this day with patience.",
  "Choose one small act of kindness.",
  "Return to the breath when scattered.",
  "Listen more than you speak.",
  "Let go of what you cannot control.",
  "Offer yourself the same compassion you give others.",
];

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

export default function Home() {
  const { user } = useAuth();
  const [journey, setJourney] = useState(null);
  const intention = INTENTIONS[new Date().getDate() % INTENTIONS.length];

  useEffect(() => { api.get("/journey").then((r) => setJourney(r.data)).catch(() => {}); }, []);

  const pathCards = [
    { to: "/alignment", icon: BookOpen, color: "#A78BFA", title: "Today's Reflection", desc: "A 2-minute lesson & reflection" },
    { to: "/games/calm-current", icon: Waves, color: "#2DD4BF", title: "Calm Current", desc: "A short breathing practice" },
    { to: "/games/wisdom-rush", icon: Brain, color: "#F5C842", title: "Wisdom Rush", desc: "Practice a skillful choice" },
    { to: "/games/karma-garden", icon: Sprout, color: "#7BC96F", title: "Karma Garden", desc: "Tend a quality today" },
  ];

  return (
    <div className="min-h-screen relative text-slate-200 overflow-hidden" data-testid="home-page">
      <Seo title="Home — Your daily path | InnerArcade" description="Your daily mindful path: today's intention, breathing, reflection and free mindful games that earn Journey XP." path="/home" noindex />
      <div className="absolute inset-0" style={{ background: "radial-gradient(1000px 600px at 15% -10%, rgba(37,52,120,0.5), transparent 60%), radial-gradient(900px 700px at 90% 0%, rgba(88,28,135,0.4), transparent 60%), radial-gradient(800px 800px at 50% 120%, rgba(14,116,144,0.3), transparent 60%), linear-gradient(180deg,#070512,#0A0A14)" }} />
      <StarField color="#A78BFA" />
      <div className="relative z-10">
        <Header />
        <div className="max-w-5xl mx-auto px-6 py-12">
          <BackButton className="mb-6" />
          <p className="text-sm tracking-[0.3em] uppercase text-violet-300/80 mb-2" data-testid="home-greeting">{greeting()}, {user?.name?.split(" ")[0] || "friend"}</p>
          <h1 className="font-head font-900 text-3xl sm:text-4xl text-slate-50 mb-2">Today&rsquo;s intention</h1>
          <p className="text-xl sm:text-2xl text-teal-300 mb-10 font-head" data-testid="home-intention">&ldquo;{intention}&rdquo;</p>

          <p className="text-sm tracking-[0.25em] uppercase text-slate-400 mb-4">Your daily path</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {pathCards.map((c, i) => {
              const Icon = c.icon;
              return (
                <Link key={i} to={c.to} data-testid={`home-path-${i}`}
                  className="group rounded-2xl border border-white/10 p-5 bg-white/[0.03] hover:-translate-y-1 transition-transform duration-300" style={{ boxShadow: `inset 0 0 0 1px ${c.color}11` }}>
                  <Icon className="w-7 h-7 mb-3" style={{ color: c.color }} />
                  <h3 className="font-head font-700 text-base text-slate-50">{c.title}</h3>
                  <p className="text-xs text-slate-400 mt-1">{c.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: c.color }}>Start <ArrowRight className="w-3 h-3" /></span>
                </Link>
              );
            })}
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/journey" data-testid="home-journey-card" className="md:col-span-2 rounded-2xl border border-[#F5C842]/25 p-6 bg-white/[0.03] hover:-translate-y-0.5 transition-transform duration-300">
              <div className="flex items-center justify-between mb-4">
                <span className="font-arcade text-lg text-[#F5C842] tracking-wider">YOUR JOURNEY</span>
                <span className="inline-flex items-center gap-1 text-sm text-slate-300"><Flame className="w-4 h-4 text-[#F5C842]" /> {journey?.streak ?? 0} day streak</span>
              </div>
              {journey ? (
                <>
                  <div className="flex items-end justify-between mb-2">
                    <span className="font-head font-900 text-3xl text-slate-50">Level {journey.level}</span>
                    <span className="text-xs text-slate-400">{journey.xp} / {journey.next_level_xp} XP</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-[#A78BFA] to-[#F5C842] transition-all duration-700" style={{ width: `${journey.progress_pct}%` }} />
                  </div>
                  <p className="text-xs text-slate-500 mt-3">{journey.stats.total_practices} practices · {journey.stats.days_practiced} days · {journey.badges.filter((b) => b.earned).length} badges earned</p>
                </>
              ) : <p className="text-sm text-slate-500">Loading your progress…</p>}
            </Link>

            <Link to="/alignment" data-testid="home-continue-card" className="rounded-2xl border border-violet-500/25 p-6 bg-white/[0.03] hover:-translate-y-0.5 transition-transform duration-300 flex flex-col justify-between">
              <div>
                <Sparkles className="w-7 h-7 text-[#A78BFA] mb-3" />
                <h3 className="font-head font-700 text-lg text-slate-50">Continue learning</h3>
                <p className="text-sm text-slate-400 mt-1">Explore your spiritual worlds and deepen a practice.</p>
              </div>
              <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#A78BFA]">Enter Alignment <ArrowRight className="w-4 h-4" /></span>
            </Link>
          </div>

          <div className="mt-12">
            <PlanPanel />
          </div>

          <div className="mt-12">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm tracking-[0.25em] uppercase text-slate-400">Mindful Games</p>
              <Link to="/mindful" data-testid="home-games-all" className="text-xs text-slate-400 hover:text-slate-200 transition-colors duration-300">See all →</Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-4" data-testid="home-games">
              {MINDFUL_GAMES.map((g) => {
                const Icon = g.icon;
                return (
                  <Link
                    key={g.id}
                    to={`/games/${g.slug}`}
                    data-testid={`home-game-${g.id}`}
                    className="group rounded-2xl border p-5 hover:-translate-y-1 transition-transform duration-300"
                    style={{ borderColor: `${g.color}44`, background: `${g.color}0d` }}
                  >
                    <Icon className="w-7 h-7 mb-3" style={{ color: g.color }} />
                    <h3 className="font-head font-700 text-base text-slate-50">{g.name}</h3>
                    <p className="text-xs uppercase tracking-wider mt-1" style={{ color: g.color }}>{g.tagline}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: g.color }}>Play <ArrowRight className="w-3 h-3" /></span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
